import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { 
  Truck, 
  MapPin,
  Clock,
  CheckCircle,
  ArrowRight,
  DollarSign,
  FileText,
  Shield
} from 'lucide-react';
import { useSectionContent } from '../hooks/useSiteContent';
import useJsonLd from '../hooks/useJsonLd';
import { JOB_POSTING_DRIVERS_GENERAL } from '../seo/schemaOrg';

// Default content values (BlueLock Freights brand; CMS overrides at runtime)
const DRIVER_DEFAULTS = {
  drivers_hero_title: "Driver Opportunities",
  drivers_hero_subtitle: "Owner-Operators, Lease-On Drivers, and Company Drivers",
  drivers_hero_tagline: "OTR and Regional Opportunities Available",
  drivers_overview_title: "Partner With BlueLock Freights",
  drivers_overview_description: "BlueLock Freights partners with qualified Owner-Operators, Lease-On Drivers, and Company Drivers seeking structured freight opportunities and consistent dispatch coordination. Our operations emphasize clear agreements, regulatory compliance, and documented settlement practices.",
  drivers_compensation_disclaimer: "Compensation varies by program type, experience, and freight availability. No income is guaranteed. Additional compensation may be available for detention, layover, driver assist, multiple stops, TONU, and specialized services when documented and approved.",
  drivers_cta_title: "Apply to Drive With BlueLock Freights",
  drivers_cta_description: "Applications are reviewed on a rolling basis."
};

const DriverHiringPage = () => {
  const { content } = useSectionContent('driver_opportunities', DRIVER_DEFAULTS);
  useJsonLd('job-drivers-general', JOB_POSTING_DRIVERS_GENERAL);

  return (
    <div className="min-h-screen bg-white" data-testid="driver-hiring-page">
      <PublicNavbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
              {content.drivers_hero_title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-200 font-medium mb-4">
              {content.drivers_hero_subtitle}
            </p>
            <p className="text-lg text-blue-100 mb-2">
              {content.drivers_hero_tagline}
            </p>
            <p className="text-base text-blue-200">
              Weekly Settlements • Structured Dispatch Support
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              {content.drivers_overview_title}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {content.drivers_overview_description}
            </p>
          </div>
        </div>
      </section>

      {/* OTR and Regional Sections */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* OTR Section */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Truck className="w-7 h-7 text-blue-900" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Over-the-Road (OTR)</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Nationwide freight opportunities</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Flexible home-time scheduling based on lane availability</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Dispatch support focused on trip planning and compliance</span>
                </li>
              </ul>
            </div>

            {/* Regional Section */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-blue-900" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Regional Opportunities</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Dedicated and regional lanes where available</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">More frequent home time</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Predictable freight patterns</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Compensation Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Compensation Structure
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {content.drivers_compensation_disclaimer}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-testid="hiring-programs-grid">
            {/* Owner-Operator Program (Dispatch-Only) */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="hiring-program-owner-operator">
              <div className="p-8">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-4">
                  Owner-Operator (Dispatch-Only)
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Fee: 8% of gross revenue or $400/week per truck</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Weekly settlements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Run under your own USDOT / MC authority</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Lease-On Program */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="hiring-program-lease-on">
              <div className="p-8">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-4">
                  Lease-On Program
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">84% / 16% revenue split on linehaul</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Weekly settlements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Truck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Run under BlueLock Transport LLC authority</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Company Driver Program */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="hiring-program-company-driver">
              <div className="p-8">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-4">
                  Company Driver
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Truck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Drive BlueLock-provided equipment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Pay structure defined in the company driver agreement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Maintenance, fuel, and insurance coordinated by BlueLock</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            Exact compensation terms are defined within individual operating agreements.
          </p>
        </div>
      </section>

      {/* Program Comparison Table */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Program Comparison
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="hiring-program-comparison-table">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b">Feature</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b">Owner-Operator</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b">Lease-On Driver</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b">Company Driver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-600">Operating Authority</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Driver's Own Authority</td>
                  <td className="px-6 py-4 text-sm text-gray-900">BlueLock Transport LLC</td>
                  <td className="px-6 py-4 text-sm text-gray-900">BlueLock Transport LLC</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-600">Compensation</td>
                  <td className="px-6 py-4 text-sm text-gray-900">8% gross or $400/wk per truck</td>
                  <td className="px-6 py-4 text-sm text-gray-900">84% / 16% linehaul split</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Per company driver agreement</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-600">Settlement Schedule</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Weekly</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Weekly</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Per agreement</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-600">Dispatch Support</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company-Coordinated</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company-Coordinated</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company-Coordinated</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-600">Equipment Ownership</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Driver-Owned</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Driver-Owned</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company-Provided</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-600">Contract Type</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Dispatch Agreement</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Lease-On Agreement</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company Driver Agreement</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            All terms are defined within individual operating agreements.
          </p>
        </div>
      </section>

      {/* Driver Requirements */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Driver Requirements
            </h2>
          </div>

          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <ul className="space-y-4">
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-blue-900" />
                </div>
                <span className="text-gray-700 pt-1">Valid CDL-A</span>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Truck className="w-4 h-4 text-blue-900" />
                </div>
                <span className="text-gray-700 pt-1">Tractor in safe operating condition</span>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-blue-900" />
                </div>
                <span className="text-gray-700 pt-1">Ability to meet FMCSA and insurance requirements</span>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-blue-900" />
                </div>
                <span className="text-gray-700 pt-1">Professional communication and reliability</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {content.drivers_cta_title}
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            {content.drivers_cta_description}
          </p>
          <Link
            to="/apply"
            data-testid="cta-apply-btn"
            className="inline-flex items-center justify-center px-10 py-4 bg-white text-blue-900 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors group"
          >
            Apply Now
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default DriverHiringPage;
